import { useEffect, useRef } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { validateCreditsForAction } from '#creditFeature/api/creditsValidation';
import { useError } from '#globalContexts/ErrorContext';

import { companiesKeys } from '../models/companies/keys';
import { useAddLeadsToEnrichmentQueue } from '../models/companies/mutations';
import { useJobProgress } from '../models/jobs/queries';

import { useJobNotifications } from './useJobNotifications';

/**
 * Contact enrichment for a single company: starts the job and tracks it until its contacts are reloaded.
 * Request failures surface through the global mutation error toast.
 *
 * @param {string} companyId
 * @param {string|null|undefined} prospectQueryId - Enrichment runs inside a search query; without one it is unavailable
 * @returns {{ enrichmentJob: Object|undefined, isStarting: boolean, startEnrichment: () => Promise<void> }}
 */
export function useCompanyContactEnrichment(companyId, prospectQueryId) {
	const enrichMutation = useAddLeadsToEnrichmentQueue();
	const queryClient = useQueryClient();
	const { showError } = useError();

	const { data: runningJobs = [] } = useJobProgress(prospectQueryId);
	useJobNotifications(runningJobs);

	const enrichmentJob = runningJobs.find(
		(j) => j.jobType === 'contactEnrichment' && ['queued', 'running'].includes(j.status),
	);

	// Bridge the gap between mutation success and the next job poll: keep the progress UI visible only
	// until the job is observed for the first time. After completion we must not fall back to "starting".
	const hasSeenJobRef = useRef(false);
	if (enrichmentJob) hasSeenJobRef.current = true;
	const isStarting =
		enrichMutation.isPending || (enrichMutation.isSuccess && !enrichmentJob && !hasSeenJobRef.current);

	const prevEnrichmentJobRef = useRef(null);
	useEffect(() => {
		if (prevEnrichmentJobRef.current && !enrichmentJob) {
			queryClient.invalidateQueries({ queryKey: companiesKeys.contacts(companyId) });
			hasSeenJobRef.current = false;
			enrichMutation.reset();
		}
		prevEnrichmentJobRef.current = enrichmentJob || null;
	}, [enrichmentJob, queryClient, companyId, enrichMutation]);

	const startEnrichment = async () => {
		const creditValidation = await validateCreditsForAction('leadEnrichment', 1);
		if (!creditValidation.hasEnoughCredits) {
			showError({ title: 'Nicht genügend Credits', message: creditValidation.error, autoClose: false });
			return;
		}
		enrichMutation.mutate({ scope: { leadIds: [companyId] }, prospectQueryId });
	};

	return { enrichmentJob, isStarting, startEnrichment };
}
